import * as React from "react";
import { observable, IObservableArray, computed } from "mobx";
import { observer } from "mobx-react";

import { Strings } from "../Stores/StringsStore";
import { ZoomInButton } from "../Buttons/Pdf/ZoomInButton";

export enum AspectRatio {
	None = 0,
	Fit = 1,
	Original = 2,
}

class Image {
	border: number = 9;
	@observable isOriginalSize: boolean = false;
	@observable initialHeight: number = 0;
	@observable initialWidth: number = 0;
	@observable aspectRatio: AspectRatio = AspectRatio.Fit;
	@observable mode: AspectRatio = AspectRatio.Fit;
	@observable imageLeft: number = 0;
	@observable imageTop: number = 0;
	@observable isLoading: boolean = true;
	@observable zoomScale: number = 1;
	@observable rotation: number = 0;
	@observable originalHeight: number = 0;
	@observable originalWidth: number = 0;
	@observable containerHeight: number = 0;
	@observable containerWidth: number = 0;
	@observable marginLeft: number = null;
	@observable isRotated: boolean = false;

	@computed get minHeight() {
		if (this.isOriginalSize) {
			return this.originalHeight * this.zoomScale;
		}
		let minHeight = this.initialHeight * this.zoomScale;
		return minHeight < this.maxHeight && minHeight > this.initialHeight ? minHeight + "px" : "0px";
	}

	@computed get minWidth() {
		if (this.isOriginalSize) {
			return this.originalWidth * this.zoomScale;
		}
		let minWidth = this.initialWidth * this.zoomScale;
		return minWidth < this.maxWidth && minWidth > this.initialWidth ? minWidth + "px" : "0px";
	}

	@computed get transformedContainerHeight() {
		return !this.isRotated ? this.containerHeight : this.containerWidth;
	}

	@computed get transformedContainerWidth() {
		return !this.isRotated ? this.containerWidth : this.containerHeight;
	}

	@computed get maxHeight() {
		return this.transformedContainerHeight * 0.9 * this.zoomScale;
	}

	@computed get maxWidth() {
		return this.transformedContainerWidth * 0.9 * this.zoomScale;
	}

	@computed get marginTop() {
		return this.isRotated ? (this.initialHeight - this.initialWidth) * this.zoomScale / 2 : 0;
	}

	get transformedMarginLeft() {
		return this.isRotated ? this.marginLeft + ((this.initialHeight - this.initialWidth) * this.zoomScale / 2) : this.marginLeft;
	}
}

export class ImageZoomerImpl {

	//const
	private readonly ZOOM_UNIT: number = 0.25;
	private readonly MAX_ZOOM_LEVEL: number = 4;
	private readonly MIN_ZOOM_LEVEL: number = 1;
	readonly IMG_ID: string = "imgID";
	readonly IMG_CONTAINER_ID: string = "divImgContainer";

	public image = new Image();
	private dragHandler = new DragHandler();
	private touchHandler = new TouchHandler();


	@computed get canZoomIn() {
		return this.image.zoomScale < this.MAX_ZOOM_LEVEL;
	}

	@computed get canZoomOut() {
		return this.image.zoomScale > this.MIN_ZOOM_LEVEL;
	}

	private canZoom = (zoomDifference: number) => {
		if (zoomDifference > 0) {
			return this.canZoomIn;
		}
		else {
			return this.canZoomOut;
		}
	}

	/**
	 * Zoom in and zoom out image
	 */
	private zoom(zoomDifference: number, mouseX: number = null, mouseY: number = null): boolean {
		if (!this.canZoom(zoomDifference)) {
			//restrict zoom to normal size
			return false;
		}
		if (!mouseX) {
			mouseX = this.image.containerWidth / 2;
		}
		if (!mouseY) {
			mouseY = this.image.containerHeight / 2;
		}
		mouseX -= this.image.marginLeft;	// position of mouse pointer with respect to image don't count margin left
		mouseY -= this.image.marginTop;

		this.dragHandler.coordX = this.image.imageLeft;
		this.dragHandler.coordY = this.image.imageTop;
		let containerRect = document.getElementById(this.IMG_CONTAINER_ID).getBoundingClientRect();
		let imgX = (mouseX - containerRect.left - this.dragHandler.coordX) / this.image.zoomScale;	// find current X on image based on zoomed size
		let imgY = (mouseY - containerRect.top - this.dragHandler.coordY) / this.image.zoomScale;	// find current Y on image based on zoomed size

		this.image.zoomScale += zoomDifference;

		let newEventPOSX = (imgX * this.image.zoomScale) + containerRect.left;
		let newEventPOSY = (imgY * this.image.zoomScale) + containerRect.top;
		let left = mouseX - newEventPOSX;	//find left so that image will be focused mouse pointer
		let top = mouseY - newEventPOSY;	//find top so that image will be focused mouse pointer

		this.setPosition(left, top, zoomDifference > 0, this.image.zoomScale);

		this.image.aspectRatio = (this.image.zoomScale == 1) ? (this.image.isOriginalSize ? AspectRatio.Original : AspectRatio.Fit) : AspectRatio.None;
		return true;
	}

	/**
	 *  Restrict moving image out of screen
	 * @param left
	 * @param top
	 * @param zoomScale
	 */
	private setPosition(left: number, top: number, isZoomingIn: boolean, zoomScale: number) {
		if ((this.image.zoomScale == 1) && !this.dragHandler.isDragging && !this.image.isOriginalSize) {
			left = 0;
			top = 0;
		}
		else {
			left = this.calculateLeft(left, isZoomingIn, zoomScale);
			top = this.calculateTop(top, isZoomingIn, zoomScale);
			if (!isZoomingIn && !this.dragHandler.isDragging) {
				if (this.dragHandler.leftDisplacement != 0) {
					let displacement = this.dragHandler.leftDisplacement / (((this.image.zoomScale + this.ZOOM_UNIT) - 1) / this.ZOOM_UNIT);
					left -= displacement;
					this.dragHandler.leftDisplacement -= displacement;
				}
				if (this.dragHandler.topDisplacement != 0) {
					let displacement = this.dragHandler.topDisplacement / (((this.image.zoomScale + this.ZOOM_UNIT) - 1) / this.ZOOM_UNIT);
					top -= displacement;
					this.dragHandler.topDisplacement -= displacement;
				}
			}
		}
		// Move image inside above restricted area
		this.image.imageLeft = left;
		this.image.imageTop = top;
	}

	private calculateLeft(left: number, isZoomingIn: boolean, zoomScale: number): number {
		let img = document.getElementById(this.IMG_ID);
		let imgContRect = document.getElementById(this.IMG_CONTAINER_ID).getBoundingClientRect();
		let imgRect = img.getBoundingClientRect();
		let imgWidth = this.imgWidth(img);

		if (left > 0) {
			left = 0;
		}

		let maxLeft = this.image.initialWidth * zoomScale * -1;
		if ((!this.image.isOriginalSize || this.image.initialWidth < imgContRect.width) && maxLeft > left) {
			left = maxLeft;
		}

		let flowsOverRightBorder = (imgWidth + left) < (imgContRect.width - imgContRect.left);
		if (this.image.isOriginalSize && (this.image.initialWidth > imgContRect.width) && flowsOverRightBorder) {
			left = this.image.containerWidth - imgContRect.left - imgWidth;
		}

		let fitsInContainer = imgWidth <= imgContRect.width;
		let flowsOverLeftBorder = left + this.image.transformedMarginLeft < 0;
		if (flowsOverLeftBorder && fitsInContainer && isZoomingIn) {
			left = 0 - this.image.transformedMarginLeft;
		}

		return left;
	}

	private calculateTop(top: number, isZoomingIn: boolean, zoomScale: number): number {
		let img = document.getElementById(this.IMG_ID);
		let imgContRect = document.getElementById(this.IMG_CONTAINER_ID).getBoundingClientRect();
		let imgRect = img.getBoundingClientRect();

		if (top > 0) {
			top = 0;
		}

		let fitsInContainer = this.image.initialHeight < (window.innerHeight - imgContRect.top);
		let maxTop = this.image.initialHeight * zoomScale * -1;
		if ((!this.image.isOriginalSize || fitsInContainer) && maxTop > top) {
			top = maxTop;
		}

		if (this.image.isOriginalSize && this.image.initialHeight > (window.innerHeight - imgContRect.top) && (imgRect.height + top) < ((window.innerHeight - imgContRect.top) - imgContRect.top)) {
			top = (window.innerHeight - imgContRect.top) - imgContRect.top - imgRect.height;
		}

		fitsInContainer = imgRect.height <= this.image.containerHeight;
		let flowsOverTopBorder = top < 0;
		if (flowsOverTopBorder && fitsInContainer && isZoomingIn) {
			top = 0;
		}

		return top;
	}

	rotateClockwise = () => {
		if (this.image.rotation === 270) {
			this.image.rotation = 0;
		}
		else {
			this.image.rotation += 90;
		}
		this.image.isRotated = this.image.rotation === 90 || this.image.rotation === 270;
		this.image.imageLeft = 0;
		this.image.imageTop = 0;
		this.image.zoomScale = 1;
		let img = document.getElementById(this.IMG_ID);
		window.setTimeout(this.setMargins, 5, img);
	}

	rotateCounterClockwise = () => {
		if (this.image.rotation === 0) {
			this.image.rotation = 270;
		}
		else {
			this.image.rotation -= 90;
		}
		this.image.isRotated = this.image.rotation === 90 || this.image.rotation === 270;
		this.image.imageLeft = 0;
		this.image.imageTop = 0;
		this.image.zoomScale = 1;
		let img = document.getElementById(this.IMG_ID);
		window.setTimeout(this.setMargins, 5, img);
	}

	private startDrag = (event: React.SyntheticEvent<any>, clientX: number, clientY: number): boolean => {
		if (this.image.isLoading) { return false };
		//about to move image after zoom
		this.dragHandler.offsetX = clientX;
		this.dragHandler.offsetY = clientY;

		// calculate integer values for top and left 
		// properties
		let targ = event.target as any;
		this.dragHandler.coordX = parseInt(targ.style.left);
		this.dragHandler.coordY = parseInt(targ.style.top);
		this.dragHandler.isDragging = true;

		return false;
	}

	private keepDragging = (clientX: number, clientY: number): boolean => {
		if (!this.dragHandler.isDragging || this.image.isLoading) { return false };
		// move div element within borders
		if (this.dragHandler.leftDisplacementStart == 0) {
			this.dragHandler.leftDisplacementStart = parseInt(document.getElementById(this.IMG_ID).style.left);
		}
		if (this.dragHandler.topDisplacementStart == 0) {
			this.dragHandler.topDisplacementStart = parseInt(document.getElementById(this.IMG_ID).style.top);
		}
		let left = this.dragHandler.coordX + clientX - this.dragHandler.offsetX;
		let top = this.dragHandler.coordY + clientY - this.dragHandler.offsetY;
		this.setPosition(left, top, false, this.image.zoomScale - 1);
		return false;
	}

	private endDrag = (): void => {
		if (this.dragHandler.isDragging) {
			if (this.dragHandler.leftDisplacementStart != 0) {
				this.dragHandler.leftDisplacement += parseInt(document.getElementById(this.IMG_ID).style.left) - this.dragHandler.leftDisplacementStart;
				this.dragHandler.leftDisplacementStart = 0;
			}
			if (this.dragHandler.topDisplacementStart != 0) {
				this.dragHandler.topDisplacement += parseInt(document.getElementById(this.IMG_ID).style.top) - this.dragHandler.topDisplacementStart;
				this.dragHandler.topDisplacementStart = 0;
			}
		}
		this.dragHandler.isDragging = false;
	}

	private getZoomDifference(isZoomingIn: boolean) {
		return isZoomingIn ? this.ZOOM_UNIT : (-1 * this.ZOOM_UNIT);
	}

	imgOverlayTouch = (event: React.TouchEvent<any>) => {
		if (this.image.isLoading) { return false; }
		let positions = this.touchHandler.registerTouches(event);
		let touches = event.changedTouches;
		//Two touches, assume it as pinch
		if (touches.length == 2 && this.touchHandler.touchesInAction.length > 1) {
			event.preventDefault();
			return this.handleMultiTouch(event, false);
		}
		return true;
	}

	/**
	 * Touch handler for touch devices
	 * This catch all touch event and based on touch event generate corresponding mouse event
	 * @param event
	 */
	handleTouch = (event: React.TouchEvent<any>) => {
		if (this.image.isLoading) { return false; }
		event.stopPropagation();
		let positions = this.touchHandler.registerTouches(event);
		let touches = event.changedTouches;
		//Two touches, assume it as pinch
		if (this.touchHandler.touchesInAction.length > 1) {
			if (this.dragHandler.isDragging) {
				this.endDrag();
			}
			event.preventDefault();
			return this.handleMultiTouch(event, true);
		}
		else {
			let clientX = positions[0];
			let clientY = positions[1];
			//one touch, move or double click
			switch (event.type) {
				case "touchstart":
					if (!this.dragHandler.isDragging) {
						if (Date.now() - this.touchHandler.timer < 300) {
							event.preventDefault();
							return this.doubleTap();
						}
						else {
							event.preventDefault();
							this.touchHandler.timer = Date.now();
							return this.startDrag(event, clientX, clientY);
						}
					}
				case "touchmove":
					event.preventDefault();
					return this.keepDragging(clientX, clientY);
				case "touchend":
					event.preventDefault();
					return this.endDrag();
				default:
					event.preventDefault();
					return false;
			}
		}
	}

	handleMultiTouch = (event: React.TouchEvent<any>, calculateZoomPosition: boolean) => {
		try {
			let touches = event.changedTouches;
			let firstTouch = this.touchHandler.touchesInAction.filter(t => t.identifier === touches[0].identifier)[0];
			let secondTouch = this.touchHandler.touchesInAction.filter(t => t.identifier === touches[1].identifier)[0];
			let firstXDistance = touches[0].pageX - firstTouch.pageX;	/* x-distance moved since touchstart */
			let firstYDistance = touches[0].pageY - firstTouch.pageY;	/* y-distance moved since touchstart */
			let secondXDistance = touches[1].pageX - secondTouch.pageX;	/* x-distance moved since touchstart */
			let secondYDistance = touches[1].pageY - secondTouch.pageY;	/* y-distance moved since touchstart */
			// Consider pinch only for certain
			if (!(Math.max(Math.abs(firstXDistance), Math.abs(firstYDistance), Math.abs(secondXDistance), Math.abs(secondYDistance)) > this.touchHandler.MIN_PICH_UNIT)) {
				return false;
			}

			if ((firstXDistance != 0) || (firstYDistance != 0) || (secondXDistance != 0) || (secondYDistance != 0)) {
				// (X1+X2)/2 is X(middle of two touch) and same for Y
				let clientX = calculateZoomPosition ? (firstTouch.clientX + secondTouch.clientX) / 2 : null;
				let clientY = calculateZoomPosition ? (firstTouch.clientY + secondTouch.clientY) / 2 : null;

				//set X and Y to new value for next pinch event
				firstTouch.pageX = touches[0].pageX;
				firstTouch.pageY = touches[0].pageY;

				secondTouch.pageX = touches[1].pageX;
				secondTouch.pageY = touches[1].pageY;

				let zoomXMax = Math.abs(firstXDistance) > Math.abs(secondXDistance) ? firstXDistance : secondXDistance;
				let zoomYMax = Math.abs(firstYDistance) > Math.abs(secondYDistance) ? firstYDistance : secondYDistance;

				let isZoomingIn: boolean;
				//Based on direction moved and x,y value find pinch in and pich out
				if (Math.abs(zoomXMax) > Math.abs(zoomYMax)) {
					if (firstTouch.clientX < secondTouch.clientX) {
						if (Math.abs(firstXDistance) > Math.abs(secondXDistance)) {
							isZoomingIn = Math.abs(firstXDistance) != firstXDistance;
						}
						else {
							isZoomingIn = Math.abs(secondXDistance) == secondXDistance;
						};
					}
					else {
						if (Math.abs(firstXDistance) > Math.abs(secondXDistance)) {
							isZoomingIn = Math.abs(firstXDistance) == firstXDistance;
						}
						else {
							isZoomingIn = Math.abs(secondXDistance) != secondXDistance;
						};
					}
				}
				else {
					if (firstTouch.clientY < secondTouch.clientY) {

						if (Math.abs(firstYDistance) > Math.abs(secondYDistance)) {
							isZoomingIn = Math.abs(firstYDistance) != firstYDistance;
						}
						else {
							isZoomingIn = Math.abs(secondYDistance) == secondYDistance;
						}
					}
					else {
						if (Math.abs(firstYDistance) > Math.abs(secondYDistance)) {
							isZoomingIn = Math.abs(firstYDistance) == firstYDistance;
						}
						else {
							isZoomingIn = Math.abs(secondYDistance) != secondYDistance;
						}
					}
				}

				// based on pinch in and pinch out trigger zoom in or zoom out
				switch (event.type) {
					case "touchstart":
					case "touchend":
						event.preventDefault();
						return true;
					case "touchmove":
						event.preventDefault();
						return this.zoom(this.getZoomDifference(isZoomingIn), clientX, clientY);
					default: return false;
				}
			}
		}
		catch (e) {
		}
		return true;
	}

	/**
	 * ZoomIn/ZoomOut on mousewheel scroll
	 * @param event
	 */
	handleScroll = (event: React.WheelEvent<any>) => {
		if (this.image.isLoading) { return false };
		event.preventDefault();
		this.dragHandler.isDragging = false;
		let direction: string = (event.deltaY > 0) ? "down" : "up";
		let isZoomingIn = (direction == "up");
		return this.zoom(this.getZoomDifference(isZoomingIn), event.clientX, event.clientY);
	}

	zoomButtonClick = (isZoomingIn: boolean, event: React.SyntheticEvent<any>) => {
		return this.zoom(this.getZoomDifference(isZoomingIn));
	}

	onDropDownChange = (event: React.SyntheticEvent<HTMLSelectElement>) => {
		let newZoomUnit = parseInt(event.currentTarget.value) / 100;
		return this.zoom(newZoomUnit - this.image.zoomScale);
	}

	/**
	 * This trigger zoom or mouse move event
	 * @param event
	 */
	onMouseDown = (event: React.MouseEvent<any>) => {
		event.preventDefault();
		return this.startDrag(event, event.clientX, event.clientY);
	}

	/**
	 * Enables image dragging after zoom
	 * @param event
	 */
	onMouseMove = (event: React.MouseEvent<any>) => {
		event.preventDefault();
		return this.keepDragging(event.clientX, event.clientY);
	}

	/**
	 * On mouse key up stop dragging functionality
	 * @param event
	 */
	onMouseUp = (event: React.MouseEvent<any>) => {
		event.preventDefault();
		this.endDrag();
	}

	/**
	 * Handle aspect ratio using toolbar button
	 * @param aspectRatio
	 */
	handleAspectRatio = (aspectRatio: AspectRatio) => {
		this.image.mode = aspectRatio;
		this.image.imageLeft = 0;
		this.image.imageTop = 0;
		this.dragHandler.leftDisplacement = 0;
		this.dragHandler.topDisplacement = 0;
		this.image.zoomScale = 1;
		this.image.aspectRatio = aspectRatio;
		this.image.marginLeft = null;
		this.image.isOriginalSize = (aspectRatio === AspectRatio.Original);
		let img = document.getElementById(this.IMG_ID);
		window.setTimeout(this.setMargins, 5, img);
	}

	/**
	 *  Store image dimension after image load
	 * @param param0
	 */
	onImgLoad = ({ target: img }: any) => {
		this.image.originalHeight = this.imgHeight(img);
		this.image.originalWidth = this.imgWidth(img);
		let container = document.getElementById(this.IMG_CONTAINER_ID);
		this.image.containerHeight = container.offsetHeight;
		this.image.containerWidth = container.offsetWidth;
		this.image.isLoading = false;
		img = document.getElementById(this.IMG_ID);
		window.setTimeout(this.setMargins, 5, img);
	}

	private imgHeight(img: any) {
		let style = getComputedStyle(img);
		return !this.image.isRotated
			? img.offsetHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom) - this.image.border - this.image.border
			: img.offsetWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - this.image.border - this.image.border;
	}

	private imgWidth(img: any) {
		let style = getComputedStyle(img);
		return !this.image.isRotated
			? img.offsetWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - this.image.border - this.image.border
			: img.offsetHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom) - this.image.border - this.image.border;
	}

	setMargins = (img: any) => {
		let height = this.imgHeight(img) / this.image.zoomScale;
		let width = this.imgWidth(img) / this.image.zoomScale;
		this.image.initialHeight = height;
		this.image.initialWidth = width;
		this.image.marginLeft = !this.image.isOriginalSize
			? (this.image.containerWidth - img.offsetWidth / this.image.zoomScale) / 2
			: this.image.isRotated ? (img.offsetHeight - img.offsetWidth) / this.image.zoomScale / 2 : 0;
	}

	/**
	 * Double tapping on image
	 */
	doubleTap = (clientX?: number, clientY?: number) => {
		let zoomLevel = this.MAX_ZOOM_LEVEL / 2;
		if (this.image.zoomScale >= zoomLevel) {
			zoomLevel = 1;
		}
		return this.zoom(zoomLevel - this.image.zoomScale, clientX, clientY);
	}

	/**
	 * Reset image to fit screen
	 */
	resetImage = () => {
		this.image.zoomScale = 1;
		this.image.aspectRatio = AspectRatio.Fit;
		this.image.imageLeft = 0;
		this.image.imageTop = 0;
		this.image.originalHeight = 0;
		this.image.originalWidth = 0;
		this.image.containerHeight = 0;
		this.image.containerWidth = 0;
		this.image.marginLeft = null;
		this.image.isOriginalSize = false;
		this.image.isLoading = true;
		this.dragHandler.leftDisplacement = 0;
		this.dragHandler.topDisplacement = 0;
		let img = document.getElementById(this.IMG_ID);
		this.onImgLoad({ target: img });
	}

	/**
	 * Resizewindow
	 */
	windowResizing = () => {
		let container = document.getElementById(this.IMG_CONTAINER_ID);
		this.image.containerHeight = container.offsetHeight;
		this.image.containerWidth = container.offsetWidth;
		let img = document.getElementById(this.IMG_ID);
		window.setTimeout(this.setMargins, 5, img);
	}
}

class DragHandler {
	isDragging: boolean = false;
	coordX: any;
	coordY: any;
	offsetX: any;
	offsetY: any;
	leftDisplacement: number = 0;
	leftDisplacementStart: number = 0;
	topDisplacement: number = 0;
	topDisplacementStart: number = 0;
}

class TouchInAction {
	clientX: number;
	clientY: number;
	identifier: number;
	pageX: number;
	pageY: number;
}

class TouchHandler {
	readonly MIN_PICH_UNIT: number = 10;
	readonly MAX_TIMESPAN_FOR_DOUBLE_TAP = 300;

	touchesInAction: TouchInAction[] = [];
	timer: number = 0;

	registerTouches = (event: React.TouchEvent<any>): number[] => {
		let touches = event.changedTouches;
		let first = touches[0];
		let clientX = 0;
		let clientY = 0;
		let touchType = "";
		for (let i = 0; i < touches.length; i++) {
			let touch = touches[i];
			if (event.type === "touchstart") {
				//Catch all touches
				if (!this.touchesInAction.some(t => t.identifier === touch.identifier)) {
					let cachedTouch = new TouchInAction();
					cachedTouch.clientX = touch.clientX;
					cachedTouch.clientY = touch.clientY;
					cachedTouch.identifier = touch.identifier;
					cachedTouch.pageX = touch.pageX;
					cachedTouch.pageY = touch.pageY;
					this.touchesInAction.push(cachedTouch);
				}
			}
			else if (event.type === "touchend") {
				//Remove cached touch events
				clientX = 0;
				clientY = 0;
				this.touchesInAction = this.touchesInAction.filter(t => t.identifier !== touch.identifier);
			}
			if (this.touchesInAction.length == 1) {
				//One touch, catch X and Y
				clientX = touches[0].clientX;
				clientY = touches[0].clientY;
				break;
			}
		}
		return [clientX, clientY];
	}
}